import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, throwIfNotOk } from '../lib/api';
import { clearClientIdentity, consumeReturnTo, getValidIdToken } from '../lib/auth';
import { qk } from '../lib/queryKeys';
import { LoadingShell, ErrorState } from '@siliconx/shared/ui/state';
import { Button } from '@siliconx/shared/ui/Button';
import { Card } from '@siliconx/shared/ui/Card';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { TOS_TEXT, TOS_VERSION } from '@siliconx/shared';
import type { TosResponse } from '@siliconx/shared';

/**
 * SIL-1008 — the ToS clickwrap acceptance screen. `TosGate` (__root.tsx)
 * redirects here from every other customer-portal route while
 * `tosAccepted` is false. GET /api/client/tos and POST /api/client/tos/accept
 * are both allowlisted at the API-middleware level (clientAuth.ts) so this
 * page is reachable even before acceptance — but "allowlisted" there only
 * bypasses the 403 tos_required check; both still require a valid Cognito
 * session and 401 for a caller with none.
 *
 * No optimistic "accepted" state, per the design spec: a failed accept call
 * leaves the user on this page with an inline error, not a silently cleared
 * gate.
 *
 * 2026-09-20 live-site audit finding: /tos is now in __root.tsx's public
 * route group (isPublicPortalPath) alongside /signup, so a signed-out
 * visitor — following the Terms link from the signup form/footer, an email,
 * or a bookmark — can land here directly with no session at all. This
 * component now branches on that: with no ID token, it skips the
 * authenticated fetch entirely (it would just 401) and renders the SAME
 * static text (`TOS_TEXT`/`TOS_VERSION`, packages/shared/src/dto/tos.ts —
 * the exact constants GET /api/client/tos serves) read-only, with no
 * accept checkbox — accepting terms without a session to record the
 * acceptance against is meaningless. A signed-in visitor (ID token present)
 * gets the exact same dynamic fetch + accept flow as before; this check
 * only ever widens who can READ the page, never narrows the existing
 * authenticated behavior.
 *
 * 2026-09-21 fix: because /tos is public, __root.tsx deliberately skips
 * `silentRefreshOrRedirect()` for it (every other route gets that check).
 * A raw token-presence check therefore treated an EXPIRED-but-still-stored
 * token as a session, fired the authenticated fetch, got a 401, and
 * rendered it as a raw "Unauthorized" error via ErrorState. Use
 * `getValidIdToken()` (packages/customer/src/lib/auth.ts) instead — it
 * silently attempts a refresh before giving up — so an expired token
 * either recovers into a real session or falls back to the signed-out
 * read-only view instead of surfacing the 401 to the user.
 *
 * SIL-1187 fix: accepting used to only invalidate the identity cache and
 * leave the user parked on this form — no navigation at all. TosGate
 * (__root.tsx) now stashes the originally-requested destination via
 * `stashReturnTo(location.href)` before bouncing here; `handleAgree` below
 * restores it via `consumeReturnTo()`, falling back to /dashboard for a
 * user who landed on /tos directly (bookmark, footer link). Deliberately a
 * HARD navigation (`window.location.replace`), same reasoning as
 * `auth/callback.tsx`'s `restoreReturnTo`: `consumeReturnTo()` can return a
 * path plus a query string, and TanStack's `navigate({ to })` treats `to`
 * as a path pattern only, silently dropping the query — a hard nav honours
 * the stashed URL verbatim. `stashReturnTo` already rejects anything that
 * isn't a same-origin, single-leading-slash relative path, so this never
 * becomes an open redirect.
 */
export const Route = createFileRoute('/tos')({
  component: TosPage,
});

// SIL-1187 — exported for the accept -> navigate regression test. Hard
// navigation on purpose; see handleAgree's docstring above.
export function restoreAfterAccept(returnTo: string | null): void {
  window.location.replace(returnTo || '/dashboard');
}

// Exported for the signed-out/signed-in rendering test (2026-09-20 audit finding).
export function TosPage() {
  const queryClient = useQueryClient();

  // Resolved once on mount via getValidIdToken() — null means "still
  // resolving" so the render logic doesn't flash the signed-out view during
  // the brief silent-refresh check. A session established or lost mid-visit
  // (e.g. sign in from another tab) doesn't need this page to react live; a
  // reload picks it up like any other route.
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getValidIdToken();
      if (!cancelled) setHasSession(token !== null);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const [tos, setTos] = useState<TosResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (hasSession === null) return;
    if (hasSession === false) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await api.api.client.tos.$get();
      await throwIfNotOk(res);
      const data = (await res.json()) as TosResponse;
      if (!cancelled) setTos(data);
    })()
      .catch((err: unknown) => {
        if (cancelled) return;
        // BUG-01 (2026-09-21): a live Cognito token the API still rejects
        // (mid-signup, no client_user row yet) is "no session" for this
        // public page - show the read-only Terms, not a 401 card. The
        // effect re-runs with hasSession=false and clears `loading`.
        if ((err as { status?: number }).status === 401) {
          setHasSession(false);
          return;
        }
        setLoadError((err as Error).message || 'Failed to load Terms of Service.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasSession]);

  const handleAgree = useCallback(async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await api.api.client.tos.accept.$post();
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }

      // tosAccepted is derived server-side from this same acceptance row —
      // drop the memoized identity so TosGate's next read reflects it
      // immediately instead of the pre-accept value for the rest of the
      // session (same pattern as individual-profile.tsx's post-save refresh).
      clearClientIdentity();
      await queryClient.invalidateQueries({ queryKey: qk.identity() });
      restoreAfterAccept(consumeReturnTo());
    } catch (ex: unknown) {
      // No optimistic "accepted" state — the user stays on /tos with a
      // visible error, and the gate is never silently cleared.
      setSubmitError((ex as Error).message || 'Failed to record acceptance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [agreed, submitting, queryClient]);

  if (hasSession === null || loading) {
    return <LoadingShell rows={6} />;
  }

  if (hasSession === false) {
    return (
      <div className="mx-auto max-w-lg space-y-6">
        <PageHeader title="Terms of Service" />
        <Card className="space-y-5">
          <pre className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-muted p-4 text-sm text-foreground">
            {TOS_TEXT}
          </pre>
          <p className="text-sm text-muted-foreground">
            Version {TOS_VERSION}.{' '}
            <a href="/dashboard" className="text-brand-600 underline">
              Sign in
            </a>{' '}
            to accept these terms.
          </p>
        </Card>
      </div>
    );
  }

  if (loadError || !tos) {
    return <ErrorState error={loadError ?? 'Failed to load Terms of Service.'} />;
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <PageHeader title="Terms of Service" pinTo="/tos" />

      <Card className="space-y-5">
        <pre className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-muted p-4 text-sm text-foreground">
          {tos.text}
        </pre>

        <label className="flex items-start gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={submitting}
          />
          <span>I have read and agree to the Terms of Service (version {tos.version})</span>
        </label>

        <div className="flex items-center gap-3">
          <Button type="button" disabled={!agreed || submitting} onClick={handleAgree}>
            {submitting ? 'Submitting…' : 'Agree & Continue'}
          </Button>
          {submitError && <span className="text-sm text-red-600">{submitError}</span>}
        </div>
      </Card>
    </div>
  );
}
